import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { billingApi } from "@/lib/billing-api";
import { usePlan } from "@/lib/use-plan";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Mock checkout page. Hosted at /mock-checkout?session_id=...
 * Stripe will replace this entire page once live keys land — this is here so
 * the full upgrade flow is testable today.
 */
export default function MockCheckout() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const navigate = useNavigate();
  const { refresh } = usePlan();
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState("4242 4242 4242 4242");
  const [exp, setExp] = useState("12 / 30");
  const [cvc, setCvc] = useState("123");

  async function pay(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionId) {
      toast.error("Missing session id.");
      return;
    }
    setBusy(true);
    try {
      await billingApi.mockComplete(sessionId);
      await refresh();
      navigate("/app/settings/billing?checkout=success", { replace: true });
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Mock checkout failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-bone/40 grid place-items-center px-6 py-16"
      data-testid="mock-checkout-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <Lock className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Mock checkout — preview environment
          </span>
        </div>
        <div className="border border-border bg-background p-8">
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Complete your subscription</p>
          <h1 className="font-display text-3xl">Pay securely</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Stripe Checkout will replace this page in production. For now, click pay to simulate a successful charge.
          </p>

          <form onSubmit={pay} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="card" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Card number
              </Label>
              <div className="relative mt-1.5">
                <Input id="card" value={card} onChange={(e: any) => setCard(e.target.value)}
                  data-testid="mock-card-number"
                  className="h-11 rounded-none pl-9 font-mono tracking-[0.05em]" />
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Expiry</Label>
                <Input id="exp" value={exp} onChange={(e: any) => setExp(e.target.value)}
                  data-testid="mock-card-exp"
                  className="mt-1.5 h-11 rounded-none font-mono" />
              </div>
              <div>
                <Label htmlFor="cvc" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">CVC</Label>
                <Input id="cvc" value={cvc} onChange={(e: any) => setCvc(e.target.value)}
                  data-testid="mock-card-cvc"
                  className="mt-1.5 h-11 rounded-none font-mono" />
              </div>
            </div>

            <Button type="submit" disabled={busy}
              data-testid="mock-pay-btn"
              className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12 mt-2">
              {busy ? "Processing…" : "Pay & subscribe"}
            </Button>

            <Button type="button" variant="outline" asChild
              data-testid="mock-cancel-btn"
              className="w-full rounded-none border-foreground/20 hover:bg-bone h-11">
              <Link to="/app/settings/billing?checkout=cancel">Cancel</Link>
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}
